// Live camera feed as a full-screen quad drawn behind the 3D scene.
//
// The crop is explicit (HANDOVER Phase 3). The video is cover-fitted to the
// viewport: scaled so the shorter relative dimension fills, and the excess
// on the other axis is cropped symmetrically. When the sides are cropped
// (the normal case on the iPad: 4:3 stream, ~0.69 screen), the visible
// vertical field of view equals the camera's full vertical field of view,
// so PerspectiveCamera.fov can be calibrated directly.
import * as THREE from 'three';
import { config } from '../config.js';
import { storage } from '../util/storage.js';
import { Backdrop } from './Backdrop.js';

const ROTATION_KEY = 'mw.backdrop.rotation';

export class CameraBackdrop extends Backdrop {
  constructor({ stream, log = () => {} }) {
    super();
    this.stream = stream;
    this.log = log;
    this.viewport = { width: 1, height: 1 };
    this.rotationSetting = storage.get(ROTATION_KEY) ?? config.backdrop.rotation;
    this.crop = { rotation: 0, axis: 'none', percent: 0, scaleX: 1, scaleY: 1 };

    const texture = new THREE.VideoTexture(stream.video);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    texture.center.set(0.5, 0.5);
    this.texture = texture;

    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.material = new THREE.MeshBasicMaterial({ map: texture, depthTest: false, depthWrite: false });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.material);
    this.scene.add(this.mesh);

    stream.on('active', () => this.fit());
  }

  get kind() {
    return 'camera';
  }

  get ready() {
    return this.stream.status === 'active' && this.stream.video.readyState >= 2;
  }

  get needsCalibration() {
    return true;
  }

  async start() {
    return this.stream.start();
  }

  stop() {
    this.stream.stop();
  }

  resize(width, height) {
    this.viewport = { width, height };
    this.fit();
  }

  /** 'auto' | 0 | 90 | -90. Persisted, because auto may guess wrong on a real device. */
  setRotation(setting) {
    this.rotationSetting = setting === 'auto' ? 'auto' : Number(setting);
    storage.set(ROTATION_KEY, this.rotationSetting);
    this.fit();
  }

  fit() {
    const video = this.stream.video;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const { width: sw, height: sh } = this.viewport;
    if (!vw || !vh || !sw || !sh) return;

    let rotation = this.rotationSetting;
    if (rotation === 'auto') rotation = vw > vh !== sw > sh ? 90 : 0;
    const rotated = rotation === 90 || rotation === -90;

    // Aspect of the video as it will be shown, and of the screen.
    const videoAspect = rotated ? vh / vw : vw / vh;
    const screenAspect = sw / sh;

    // Screen-space scale of the sampled region: one axis is 1, the other < 1.
    let scaleX = 1;
    let scaleY = 1;
    let axis = 'none';
    if (videoAspect > screenAspect + 1e-6) {
      scaleX = screenAspect / videoAspect; // video wider than screen: crop sides
      axis = 'sides';
    } else if (videoAspect < screenAspect - 1e-6) {
      scaleY = videoAspect / screenAspect; // video taller than screen: crop top/bottom
      axis = 'top/bottom';
    }

    // Texture transform is centre + S * R * (uv - centre), with S in texture
    // axes, so the screen-space scales swap when the texture is rotated.
    const t = this.texture;
    t.rotation = THREE.MathUtils.degToRad(rotation);
    t.repeat.set(rotated ? scaleY : scaleX, rotated ? scaleX : scaleY);
    t.offset.set(0, 0);

    const percent = Math.round((1 - Math.min(scaleX, scaleY)) * 100);
    const changed = rotation !== this.crop.rotation || axis !== this.crop.axis || percent !== this.crop.percent;
    this.crop = { rotation, axis, percent, scaleX, scaleY };
    if (changed) {
      this.log(`backdrop fit: video ${vw}x${vh} rot ${rotation} -> crop ${axis} ${percent}%`);
    }
  }

  render(renderer) {
    if (!this.ready) return;
    renderer.render(this.scene, this.camera);
  }

  getInfo() {
    const c = this.crop;
    return {
      rotation: `${this.rotationSetting}${this.rotationSetting === 'auto' ? ` (${c.rotation})` : ''}`,
      crop: c.axis === 'none' ? 'none' : `${c.axis} ${c.percent}%`,
      fovNote:
        c.axis === 'top/bottom'
          ? `visible vFOV = ${(c.scaleY * 100).toFixed(0)}% of camera vFOV`
          : 'visible vFOV = camera vFOV',
    };
  }
}
